<template>
  <div class="list-view has-background-white custom-fields-card pl-0 templates-table-col">
    <div class="column">
      <div class="columns">
        <div class="column">
          <table-toolbar searchPlaceHolder="Search Templates" :toolbarOpts="tableColOpts.toolBar" @handleAction="handleAction"></table-toolbar>
          <data-table :apiMode="activeScreen !== 'qcFormTemplates'" :tableOpts="tableColOpts" :loadData="loadData" @cell-clicked="handleAction" :key="refresh"> </data-table>
        </div>
      </div>
    </div>
  </div>
   <list-item-form
      v-if="showModal"
      :item="itemToEdit"
      :config="modalConfig"
      @close="closeModal"
      @refresh-table="refreshTable"
    />
  <activity-slider
      :isActive="isActivityActive"
      :id="companyId"
      module="Company"
      :company="companyId"
      @update:isActive="isActivityActive = false"
  />
  <create-template-order :projectId="templateProjectId"
    :editMode="editMode"
    :selectedTemplate="templateToEdit"
    :isActive="showCreateTemplateOrderModal"
    v-if="showCreateTemplateOrderModal"
    @close="resetModalProps"
    :isMaterial="$store.state.activeScreen === 'matTemplates'"/>
  <clone-template
    :active="showCloneModal"
    :templateName="cloneTemplateName"
    :isLoading="isCloneLoading"
    @close="closeCloneModal"
    @confirm="cloneTemplate"
  />
  <o-loading :active="isLoading"></o-loading>
</template>
<script>
import { onBeforeMount, reactive, ref, toRefs, computed } from "vue";
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import DataTable from "@/components/table-fields/DataTable.vue";
import TableToolbar from "@/components/table-fields/TableToolbar.vue";
import templatesListCols from "@/components/table-cols/TemplatesListCols.js";
import TableGutter from "@/components/TableGutter.vue";
import Projects from '@/models/Projects';
import SupplyChain from '@/models/SupplyChain';
import Todo from '@/models/Todo';
import Form from '@/models/Form';
import ListItemForm from '@/components/modals/ListItemForm.vue';
import { cloneDeep, get, trim, find, isEmpty } from 'lodash';
import ActivitySlider from '@/components/ActivitySlider.vue';
import CloneTemplate from '@/components/modals/CloneTemplate.vue';
import CreateTemplateOrder from '@/components/modals/CreateEditTemplateOrder.vue'
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import ProductionTemplates from '@/models/ProductionTemplates';
import MaterialTemplates from '@/models/MaterialTemplates';
import MaterialManager from '@/models/MaterialManager';

export default {
  name: "Templates",
  components: {
    DataTable,
    TableToolbar,
    TableGutter,
    'list-item-form': ListItemForm,
    ActivitySlider,
    CreateTemplateOrder,
    CloneTemplate,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();
    const activeScreen =  store.state.activeScreen;
    const company = store.state.userData?.company;

    const data = reactive({
      isActive: false,
      selectedUser: null,
      refresh: 0,
      searchText: '',
      templateProject: {},
      showModal: false,
      itemToEdit: {},
      isActivityActive: false,
      showCreateTemplateOrderModal: false,
      templateProjectId: '',
      editMode: false,
      templateToEdit: {},
      showCloneModal: false,
      isCloneLoading: false,
      cloneItem: {},
      isLoading: false,
    });

    const tableColOpts = computed(() => templatesListCols[activeScreen]);
    const updatedApiCalled = ref(false);
    const companyId = computed(() => store.state.userData?.company || '');
    const cloneTemplateName = computed(() => {
      if (activeScreen === 'qcFormTemplates') {
        return data.cloneItem?.name || '';
      }
      return data.cloneItem?.templateName || data.cloneItem?.name || '';
    });

    const loadData = async (commonParam, e) => {
      const params = {
        ...commonParam,
        search: data.searchText,
      };
      if (['prodTemplates', 'matTemplates'].includes(activeScreen)) {
        const templateProject = (await Projects.haveTemplateProject())?.templateProject;
        if (!isEmpty(templateProject)) {
          data.templateProject = templateProject;
        }
        const module = activeScreen === 'matTemplates' ? 'MatTemplates' : 'ProdTemplates';
        params.module = module;
        params.projectId = templateProject?.id;
        params.filterNoItemOrders = false
        data.templateProjectId = params.projectId;
        const resp = await SupplyChain.supplyChain(params);
        resp.data = resp.data.map(template => ({
          ...template,
          type: template.isCutOrder ? 'Cut' : 'Production',
        }));
        return resp;

      } else if (activeScreen === 'formTemplates') {
        params.company = company;
        const resp = await Todo.getAllTemplateForms(params);
        return resp;
      } else if (activeScreen === 'qcFormTemplates') {
        params.company = company;
        params.formType = 'template';
        const resp  = await Form.get(params);
        return resp.data;
      }
    };

    const handleAction = (event) => {
      if (event === 'addNew' || event.event === 'addNew') {
        data.isActive = true;
        // check for active screen and trigger the create modal for prod and material template
        if (['matTemplates', 'prodTemplates'].includes(store.state.activeScreen)) {
          data.showCreateTemplateOrderModal = true
        }
      } else if (event === 'openActivity') {
        data.isActivityActive = true;
      } else if (event.type === 'editUser') {
        data.selectedUser = event.data;
        data.isActive = true;
      } else if (event.type === 'search') {
        data.searchText = event.data;
        data.refresh++;
      } else if (event === 'addQcFormTemplate') {
        router.push({ path: `/qcFormTemplate/${company}/add` });
      } else if (event === 'addFormTemplate') {
        router.push({ path: `/formTemplate/${company}/add` });
      } else if (event.type === 'isForms') {
        router.push({ path: `/formTemplate/${company}/${event.data._id}` });  
      } else if (event.type === 'openCard') {
        if (['prodTemplates', 'matTemplates'].includes(activeScreen)) {
          router.push({ name: `${activeScreen === 'prodTemplates' ? 'production' : 'material'}-template-edit`, params: {
            projectId: data.templateProject._id,
            cardId: event.data._id,
          }})
        } else {
          // need to write common code for opening template card and then redirecting to respective template details page based on screen
          router.push({ path: `/qcFormTemplate/${company}/${event.data._id}` });
        }
      } else if (event.type === 'editTemplate') {
        if (['matTemplates', 'prodTemplates'].includes(store.state.activeScreen)) {
          data.editMode = true;
          data.templateToEdit = event.data
          data.showCreateTemplateOrderModal = true
        } else {
          openModal(event.data);
        }
      } else if (event.type === 'cloneTemplate') {
        openCloneModal(event.data);
      } else if (event.type === 'removeTemplate') {
        removeTemplate(event.data);
      }
    };

    const openCloneModal = (item = {}) => {
      data.cloneItem = cloneDeep(item);
      data.showCloneModal = true;
    };

    const closeCloneModal = () => {
      data.showCloneModal = false;
      data.cloneItem = {};
      data.isCloneLoading = false;
    };

    const cloneTemplate = async ({ name, copies }) => {
      data.isCloneLoading = true;
      try {
        for (let i = 1; i <= copies; i++) {
          const cloneName = copies > 1 ? `${name} (${i})` : name;

          if (activeScreen === 'qcFormTemplates') {
            await Form.cloneForm({
              _id: data.cloneItem._id,
              formId: data.cloneItem._id,
              name: cloneName,
              formName: cloneName,
            });
          } else if (activeScreen === 'prodTemplates') {
            const payload = {
              ...cloneDeep(data.cloneItem),
              templateName: cloneName,
              name: cloneName,
            };
            await ProductionTemplates.clone(payload);
          } else if (activeScreen === 'matTemplates') {
            const payload = {
              ...cloneDeep(data.cloneItem),
              templateName: cloneName,
              name: cloneName,
            };
            await MaterialManager.clone(payload);
          } else {
            const payload = {
              ...cloneDeep(data.cloneItem),
              name: cloneName,
              templateName: cloneName,
              formName: cloneName,
              cardType: data.cloneItem.cardType || 'templateForm',
            };
            await Todo.updateFormTemplate(payload, true);
          }
        }

        toast.success(copies > 1 ? 'Templates cloned successfully' : 'Template cloned successfully');
        closeCloneModal();
        refreshTable();
      } catch (e) {
        data.isCloneLoading = false;
        toast.error('Failed to clone template. Please try again.');
      }
    };

    const removeTemplate = (item) => {
      const templateName = item?.templateName || item?.name || 'this template';
      DialogProgrammatic.confirm({
        title: '',
        message: `<p class="is-flex is-align-items-center"><i class="has-text-danger icon-alert is-size-1 mr-2"></i>Are you sure want to Delete template ${templateName}</p>`,
        okButton: 'Yes, Delete!',
        cancelButton: 'Cancel',
        type: 'danger',
        cancelBtnIsOutlined: true,
        confirmButtonStyle: 'isSolid',
        onConfirm: async () => {
          try {
            data.isLoading = true;
            if (activeScreen === 'qcFormTemplates') {
              await Form.archive(item._id);
            } else if (activeScreen === 'prodTemplates') {
              await ProductionTemplates.archive(item);
            } else if (activeScreen === 'matTemplates') {
              await MaterialTemplates.archive(item);
            } else {
              await Todo.archiveForm(item);
            }
            toast.success('Template deleted successfully');
            refreshTable();
          } catch (e) {
            toast.error('Failed to delete template. Please try again.');
          } finally {
            data.isLoading = false;
          }
        },
      });
    };

    const resetModalProps = (opts) => {
      data.showCreateTemplateOrderModal = false
      data.editMode = false
      data.templateToEdit = {}
      if (opts?.refresh) {
        data.refresh++;
      }
    }

    const openModal = (item = {}) => {
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };
    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };
    const refreshTable = () => {
      data.refresh += 1;
    };

    const modalConfig = {
      label: activeScreen === 'qcFormTemplates' ? 'QA/QC Form' : 'Forms',
      showColor: false,
      showSubCategories: false,
      saveFn: async (payload) => {
        const templateName = trim(payload.templateName || payload.name || payload.formName || '');

        if (!templateName) {
          return {
            failedData: [
              { _errorMsg: 'Template name is required!' }
            ]
          };
        }

        const normalizedPayload = {
          ...payload,
          name: payload.name || templateName,
          formId: payload.formId || payload._id || '',
          qaqcName: payload.qaqcName || '',
          templateName,
        };

        if (activeScreen === 'qcFormTemplates') {
          return Form.updateFormTemplate(normalizedPayload);
        }
        return Todo.updateFormTemplate(normalizedPayload, false);
      },
      updateFn: async (payload) => {
        const templateName = trim(payload.templateName || payload.name || payload.formName || '');

        if (!templateName) {
          return {
            failedData: [
              { _errorMsg: 'Template name is required!' }
            ]
          };
        }

        const normalizedPayload = {
          ...payload,
          name: payload.name || templateName,
          qaqcName: payload.qaqcName || '',
          formId: payload.formId || payload._id || '',
          templateName,
        };

        if (activeScreen === 'qcFormTemplates') {
          return Form.updateFormTemplate(normalizedPayload);
        }
        return Todo.updateFormTemplate(normalizedPayload, false);
      }
    };
    return {
      ...toRefs(data),
      loadData,
      handleAction,
      tableColOpts,
      modalConfig,
      companyId,
      cloneTemplateName,
      updatedApiCalled,
      resetModalProps,
      refreshTable,
      closeModal,
      closeCloneModal,
      cloneTemplate,
      activeScreen,
    };
  },
};
</script>
<style>
::v-deep(.icon-alert:before) {
  font-size: 40px;
}
</style>